// Scoring for Map of the Week.
//
// Shared by the public page and the admin panel so a board and a month total
// can never disagree about who scored what.
//
// The rule that shapes all of this: one course per week, two submission
// windows, and a player competes in exactly ONE of them — whichever they set
// their first time in. Runs in the later window are ignored for someone who
// already ran in the earlier one.

import { fetchBestRecordInWindow } from './cs2kz';

/**
 * A player's result for one week in one mode.
 *
 * Walks the week's windows in order and stops at the first one the player has a
 * run in; their time is the fastest run inside that window.
 *
 * @returns {Promise<object|null>} null if they never ran, or the API call failed
 */
export const fetchPlayerEntry = async ({ week, player, mode }) => {
  const sessions = week.sessions || [];

  for (let i = 0; i < sessions.length; i += 1) {
    const session = sessions[i];
    let record = null;
    try {
      // eslint-disable-next-line no-await-in-loop -- windows are checked in order; the first hit locks the player in
      record = await fetchBestRecordInWindow({
        mapId: week.mapId,
        courseName: week.courseName,
        mode,
        steamId: player.steamId,
        since: session.startsAt,
        until: session.endsAt,
      });
    } catch {
      // Treat an API failure as "no run in this window" rather than failing the
      // whole board for everyone.
      record = null;
    }
    if (record) {
      return {
        uid: player.uid || player.id,
        name: player.kzPlayerName || player.username,
        steamId: player.steamId,
        sessionKey: session.key,
        sessionLabel: session.label,
        timeSeconds: record.time,
        teleports: record.teleports,
      };
    }
  }

  return null;
};

/**
 * Rank entries by time ascending, sharing rank on ties.
 * @param {Array} entries - only players who actually ran
 * @param {number[]} points - points for 1st, 2nd, 3rd, …
 */
export const rankEntries = (entries, points) => {
  const sorted = [...entries].sort((a, b) => a.timeSeconds - b.timeSeconds);

  let lastTime = null;
  let lastRank = 0;
  return sorted.map((entry, i) => {
    const rank = entry.timeSeconds === lastTime ? lastRank : i + 1;
    lastTime = entry.timeSeconds;
    lastRank = rank;
    return { ...entry, rank, points: points[rank - 1] ?? 0 };
  });
};

/** The finished board for one week in one mode. Players who didn't run are out. */
export const fetchWeekBoard = async ({ week, players, mode, points }) => {
  const entries = await Promise.all(
    players.map((player) => fetchPlayerEntry({ week, player, mode }))
  );
  return rankEntries(entries.filter(Boolean), points);
};

/**
 * Points per player across several weeks, summed over every mode the course was
 * ranked in. Used for the monthly standings in the admin panel.
 *
 * @param {{ weeks: Array, players: Array, modes: Array, points: number[] }} args
 * @returns {Promise<Array>} rows sorted by points desc, then weeks played desc
 */
export const fetchTotals = async ({ weeks, players, modes, points }) => {
  const totals = new Map();

  const boards = await Promise.all(
    weeks.flatMap((week) =>
      modes
        .filter((mode) => week.modes?.[mode.id]?.ranked)
        .map((mode) => fetchWeekBoard({ week, players, mode: mode.id, points }))
    )
  );

  boards.flat().forEach((row) => {
    const current = totals.get(row.uid) || {
      uid: row.uid,
      name: row.name,
      points: 0,
      entries: 0,
      wins: 0,
    };
    current.points += row.points;
    current.entries += 1;
    if (row.rank === 1) current.wins += 1;
    totals.set(row.uid, current);
  });

  return [...totals.values()].sort((a, b) => b.points - a.points || b.entries - a.entries);
};
